import React from 'react';

import { css } from '@emotion/react';
import { NeutralColorToken, Type } from '@robinhood/design';
import { E164ToNational, LastFourDigits, SocialSecurity } from '@x1/util-format';

import { PersonalInformationPagePrefillData } from './types';
import Button from 'src/components/creditApplication/Button';
import Cell from 'src/components/creditApplication/Cell';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import { useGlobalStore } from 'src/state/global';

interface RowProps {
  title: string;
  value: string;
}

const Row = ({ title, value }: RowProps) => {
  return (
    <div css={row}>
      <Type.Text size='M' color={NeutralColorToken.Fg2}>
        {title}
      </Type.Text>
      <Type.Text size='M' overrideRootStyles={{ textAlign: 'right' }}>
        {value}
      </Type.Text>
    </div>
  );
};

interface PropsIface {
  data: PersonalInformationPagePrefillData;
}

const PrefillInformationPage = ({ data }: PropsIface) => {
  const { nextPage, nextPageLoading } = React.useContext(NavigationContext);

  const onConfirm = React.useCallback(() => {
    useGlobalStore.setState({
      personalInformation: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        e164: data.e164,
        dateOfBirth: data.dateOfBirth,
        ssn: data.ssnLast4,
      },
    });
    nextPage();
  }, [nextPage]);

  return (
    <CreditApplicationPageLayout title='Review your information' subtitle='Make sure your information is up to date.'>
      <div css={root}>
        <Cell css={cell}>
          <Row title='First name' value={data.firstName} />
          <Row title='Last name' value={data.lastName} />
          <Row title='Date of birth' value={data.dateOfBirth} />
          <Row title='SSN' value={`••• •• ${LastFourDigits(data.ssnLast4)}`} />
          <Row title='Phone' value={E164ToNational(data.e164)} />
          <Row title='Email' value={data.email} />
        </Cell>
        <Button
          label='Confirm'
          state={nextPageLoading ? 'loading' : 'default'}
          width='full'
          type='submit'
          onClick={onConfirm}
        />
      </div>
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const cell = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const row = css`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  word-break: break-word;
`;

export default PrefillInformationPage;
